"use client";

import posthog from "posthog-js";
import { readConsent, clearAnalyticsCookies } from "@/lib/consent";

// Mesure d'audience (PostHog) — LE seul fichier du site qui parle à PostHog.
// Les composants appellent `track.quelqueChose(...)`, jamais `posthog.capture(...)` :
// un seul endroit pour renommer un événement, changer une propriété ou tout couper.
//
// ─── Où les données partent-elles ? ────────────────────────────────────────────
// UNIQUEMENT depuis atlanx.io (la prod). Sur preview.atlanx.io et en local, rien
// n'est envoyé : chaque événement est simplement affiché dans la console du
// navigateur (`[analytics] config_accessory_added {…}`). On peut donc vérifier que
// le suivi est correctement branché en développement sans polluer les statistiques.
// Le portier, c'est le nom de domaine — pas une variable à penser à changer.
//
// ─── DEUX portiers, pas un ────────────────────────────────────────────────────
// 1. le domaine (ci-dessus) ;
// 2. le CONSENTEMENT du visiteur, géré par `cookieless_mode: "on_reject"` :
//    · tant qu'il n'a pas répondu → RIEN n'est stocké, RIEN n'est capturé ;
//    · s'il accepte → cookie + mesure complète (parcours sur plusieurs visites,
//      rétention, replay) ;
//    · s'il refuse → il est quand même COMPTÉ, sans aucun stockage : PostHog
//      calcule sur ses serveurs un identifiant anonyme irréversible
//      hash(projet, sel_du_jour, IP, navigateur, domaine). Le sel change chaque
//      jour → le même visiteur est reconnu DANS la journée, jamais au-delà.
//      C'est ce qui rend cette mesure exempte de consentement (recommandation CNIL).
//    Sans ce réglage, refuser = ne plus rien mesurer du tout (décision de Jules,
//    2026-08-07 : on ne veut pas perdre 100 % des visiteurs qui refusent).
// ⚠️ Ce mode exige aussi d'activer « Cookieless server hash mode » dans les
//    réglages PostHog du projet (Web analytics), sinon les événements des
//    visiteurs sans cookie ne sont pas traités.

const PROD_HOSTS = new Set(["atlanx.io", "www.atlanx.io"]);

/** Vrai seulement sur la prod, et seulement si la clé PostHog est configurée. */
export function analyticsEnabled(): boolean {
  return (
    typeof window !== "undefined" &&
    PROD_HOSTS.has(window.location.hostname) &&
    Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY)
  );
}

// PostHog ne doit être initialisé qu'une fois par chargement de page.
let started = false;

/**
 * Allume la mesure d'audience.
 * PostHog est initialisé pour TOUT LE MONDE (c'est ce qui permet de compter les
 * visiteurs qui refusent), mais en mode `on_reject` il ne stocke rien et ne
 * capture rien tant que le visiteur n'a pas répondu au bandeau.
 * Rappelée par le bandeau à l'acceptation → d'où l'`opt_in` en fin de fonction.
 */
export function startAnalytics(): void {
  if (!analyticsEnabled()) return;
  if (started) {
    // Déjà initialisé : on ne fait qu'appliquer la décision (cas du bandeau).
    if (readConsent() === "granted") posthog.opt_in_capturing();
    return;
  }
  started = true;
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
    // On passe par notre propre domaine pour ne pas être coupé par les bloqueurs de pub
    // (relais défini dans `next.config.ts`).
    api_host: "/ingest",
    ui_host: "https://eu.posthog.com",
    // « Millésime » des réglages par défaut : fige le comportement du SDK pour que sa mise
    // à jour ne change pas silencieusement notre mesure. Inclut le suivi des changements de
    // page côté navigation interne.
    defaults: "2026-06-25",
    // Seuls les visiteurs connectés ont une fiche « personne » (les autres restent des
    // événements anonymes, 5× moins chers). Bascule en "always" si on veut analyser les
    // visiteurs anonymes dans le temps.
    person_profiles: "identified_only",
    // Erreurs JavaScript remontées automatiquement (pages blanches, plantages 3D…).
    capture_exceptions: true,
    // Cœur du dispositif : cookie si accord, comptage anonyme sans stockage si refus,
    // rien du tout tant qu'on n'a pas répondu (voir l'en-tête du fichier).
    cookieless_mode: "on_reject",
  });
  // Accord déjà mémorisé d'une visite précédente : on rallume tout de suite.
  if (readConsent() === "granted") posthog.opt_in_capturing();
}

/**
 * Refus (ou retrait) du consentement : on efface ce qui a pu être déposé.
 * ⚠️ En mode `on_reject`, `opt_out_capturing()` ne coupe PAS la mesure : il fait
 * basculer PostHog en comptage anonyme sans stockage. C'est voulu — le visiteur
 * reste compté, sans cookie ni donnée personnelle.
 */
export function stopAnalytics(): void {
  if (started) {
    posthog.opt_out_capturing();
    posthog.reset();
  }
  if (typeof window !== "undefined") clearAnalyticsCookies();
}

type Props = Record<string, unknown>;

// D'où vient le visiteur qui entre dans le configurateur. ⚠️ On ne peut PAS le
// déduire du référent : en navigation interne (liens Next), le navigateur ne le
// met pas à jour — on lirait « direct » pour quelqu'un venu de l'accueil. Les
// points d'entrée déposent donc eux-mêmes un jeton, valable le temps de l'onglet.
const CONFIG_SOURCE_KEY = "atlanx:config-source";

export function rememberConfigSource(
  source: "home" | "product" | "catalogue" | "nav" | "header",
): void {
  try {
    sessionStorage.setItem(CONFIG_SOURCE_KEY, source);
  } catch {
    /* stockage indisponible : on retombera sur le référent */
  }
}

/** Lit puis efface le jeton — une entrée dans le configurateur n'a qu'une origine. */
export function takeConfigSource(): string | null {
  try {
    const v = sessionStorage.getItem(CONFIG_SOURCE_KEY);
    sessionStorage.removeItem(CONFIG_SOURCE_KEY);
    return v;
  } catch {
    return null;
  }
}

// Méthode d'inscription en cours. Même mécanisme de jeton que ci-dessus, pour une
// raison différente : l'inscription Google quitte le site puis y revient, et c'est
// seulement AU RETOUR qu'on découvre qu'un compte a été créé — trop tard pour savoir
// par quel bouton. Le jeton survit à l'aller-retour (même onglet, même origine).
const SIGNUP_METHOD_KEY = "atlanx:signup-method";

export type SignupMethod = "email" | "google";

export function rememberSignupMethod(method: SignupMethod): void {
  try {
    sessionStorage.setItem(SIGNUP_METHOD_KEY, method);
  } catch {
    /* stockage indisponible : la méthode sera « unknown » */
  }
}

/** Lit puis efface le jeton — une inscription n'a qu'une méthode. */
export function takeSignupMethod(): SignupMethod | "unknown" {
  try {
    const v = sessionStorage.getItem(SIGNUP_METHOD_KEY);
    sessionStorage.removeItem(SIGNUP_METHOD_KEY);
    return v === "email" || v === "google" ? v : "unknown";
  } catch {
    return "unknown";
  }
}

/**
 * @param onLeave événement émis pendant que la page se ferme (`pagehide`).
 * ⚠️ Sans ce drapeau, l'événement est PERDU : PostHog empile les envois dans une file
 * qu'il vide périodiquement, et le navigateur annule les requêtes en cours dès que
 * l'onglet part. `send_instantly` court-circuite la file, et `sendBeacon` confie la
 * requête au navigateur, qui s'engage à la livrer même l'onglet fermé.
 */
function capture(event: string, props?: Props, onLeave = false): void {
  if (typeof window === "undefined") return;
  if (!analyticsEnabled()) {
    console.debug(`[analytics] ${event}`, props ?? {});
    return;
  }
  if (!started) return; // pas de consentement → l'événement est simplement abandonné
  posthog.capture(event, props, onLeave ? { transport: "sendBeacon", send_instantly: true } : undefined);
}

/**
 * Propriétés cumulées sur le visiteur (« a déjà cliqué chez un revendeur »…).
 * ⚠️ Avec le réglage `person_profiles: "identified_only"`, elles ne sont conservées
 * que pour les visiteurs CONNECTÉS ; pour les autres, l'appel ne coûte rien et
 * n'enregistre rien. Les statistiques d'usage, elles, reposent sur les événements.
 */
function setPerson(set: Props, setOnce?: Props): void {
  if (!analyticsEnabled()) {
    console.debug("[analytics] person", { set, setOnce });
    return;
  }
  if (!started) return;
  posthog.setPersonProperties(set, setOnce);
}

/** À appeler après connexion/inscription : rattache l'historique anonyme au compte. */
export function identifyUser(userId: string, props?: Props): void {
  if (!analyticsEnabled()) {
    console.debug("[analytics] identify", userId, props ?? {});
    return;
  }
  if (!started) return;
  posthog.identify(userId, props);
}

/** À appeler à la déconnexion : repart d'un visiteur anonyme. */
export function resetUser(): void {
  if (!analyticsEnabled()) {
    console.debug("[analytics] reset");
    return;
  }
  if (!started) return;
  posthog.reset();
}

// ─── Vocabulaire partagé ──────────────────────────────────────────────────────

export type ProductKindTag = "vest" | "accessory";
type AddMethod = "drag" | "tap";
type GalleryMethod = "arrow" | "thumb" | "zoom" | "swipe";
type ConfigSource = "direct" | "home" | "product" | "catalogue" | "unknown";
type FeedbackSource = "fab" | "faq" | "footer";

export const track = {
  // ── Accueil & navigation ────────────────────────────────────────────────────

  /** Boutons d'appel à l'action de l'accueil : lequel envoie vraiment vers le produit. */
  homeCtaClicked: (p: { cta: "configurator" | "catalogue"; zone: "hero" | "showcase" }) =>
    capture("home_cta_clicked", p),

  /** Question de la FAQ dépliée = une objection réelle du visiteur. */
  faqOpened: (p: { question: string }) => capture("faq_opened", p),

  /** Appels à retours de la phase de lancement. `source` dit LEQUEL des trois
   *  points d'entrée travaille — sans ça, on ne saura jamais si un appel qui ne
   *  rapporte rien est mal placé ou simplement pas vu. Le couple ouverture /
   *  envoi donne l'autre moitié : combien renoncent devant le formulaire. */
  feedbackOpened: (p: { source: FeedbackSource }) => capture("feedback_opened", p),

  feedbackSent: (p: { source: FeedbackSource; has_contact: boolean }) =>
    capture("feedback_sent", p),

  // ── Questionnaire « Ton avis » (/formulaire) ──────────────────────────────────────
  // Commencé / écran vu / terminé : le trio donne le taux d'abandon et l'écran
  // où l'on décroche. Les RÉPONSES, elles, ne passent jamais par ici (fichier
  // partagé, lu par le studio) — seul le parcours est mesuré.
  surveyStarted: (p: { source: string; lang: string }) => capture("survey_started", p),
  surveyScreenViewed: (p: { screen: string; index: number }) => capture("survey_screen_viewed", p),
  surveyCompleted: (p: { source: string; lang: string; seconds: number | null }) =>
    capture("survey_completed", p),

  themeSwitched: (p: { theme: string }) => capture("theme_switched", p),

  /** Bascule FR/EN. C'est LE signal qui décidera de l'étape 2 (vraies pages
   *  `/en/`) : sans demande anglophone mesurée, on ne la construit pas. */
  langSwitched: (p: { lang: "fr" | "en" }) => capture("lang_switched", p),

  accountCtaClicked: (p: { state: "connected" | "anonymous" }) => capture("account_cta_clicked", p),

  // ── Tunnel d'inscription ────────────────────────────────────────────────────
  // `account_cta_clicked` ne disait que l'intention ; ces trois-là disent l'issue.
  // Le funnel se lit : clic sur « Créer un compte » → formulaire envoyé → compte actif.

  /** Formulaire d'inscription accepté (email de confirmation parti, ou départ vers Google). */
  signupSubmitted: (p: { method: SignupMethod }) => capture("signup_submitted", p),

  /** Refus côté serveur : c'est ici qu'on verra POURQUOI les inscriptions n'aboutissent pas. */
  signupFailed: (p: { method: SignupMethod; reason: string }) => capture("signup_failed", p),

  /**
   * Le compte existe ET la session est ouverte : la seule vraie conversion.
   * ⚠️ Volontairement décorrélé de `signup_submitted` — par email, le compte n'est
   * actif qu'après le clic dans le mail de confirmation, parfois le lendemain.
   */
  userSignedUp: (p: { method: SignupMethod | "unknown" }) => capture("user_signed_up", p),

  // ── Catalogue ───────────────────────────────────────────────────────────────

  /** Une vue de la liste, avec les filtres actifs (lus dans l'URL). */
  catalogViewed: (p: {
    results_count: number;
    query?: string;
    category?: string;
    brand?: string;
    sort?: string;
  }) => capture("catalog_viewed", p),

  /**
   * Recherche lancée. `results_count: 0` = la liste de courses du catalogue :
   * ce que les gens cherchent et qu'on n'a pas encore.
   */
  catalogSearched: (p: { query: string; results_count: number; source: "bar" | "suggestion" }) =>
    capture("catalog_searched", p),

  catalogSuggestionClicked: (p: { query: string; suggestion: string; kind: string }) =>
    capture("catalog_suggestion_clicked", p),

  /**
   * Recherche restée sans réponse : la personne a tapé, rien n'est apparu, elle est
   * partie sans valider. Invisible autrement — et c'est pourtant LE signal « ce produit
   * nous manque ». Envoyé une fois la saisie terminée, jamais lettre par lettre.
   */
  catalogSearchEmpty: (p: { query: string }) => capture("catalog_search_empty", p),

  catalogFiltered: (p: { filter_type: "category" | "brand" | "rayon"; value: string }) =>
    capture("catalog_filtered", p),

  catalogSorted: (p: { sort: string }) => capture("catalog_sorted", p),

  /** Clic sur une carte produit. `position` = rang dans la grille (le haut convertit-il mieux ?). */
  catalogProductClicked: (p: {
    product_id: string;
    product_name: string;
    position: number;
    price_min_eur?: number;
    discount_pct?: number;
    has_price: boolean;
  }) => capture("catalog_product_clicked", p),

  catalogQuickviewOpened: (p: { product_id: string; product_name: string }) =>
    capture("catalog_quickview_opened", p),

  /** Cœur ajouté/retiré. Signal d'intention fort, et futur socle des alertes prix. */
  productFavorited: (p: { product_id: string; favorited: boolean; source: string }) =>
    capture("product_favorited", p),

  // ── Fiche produit ───────────────────────────────────────────────────────────

  productViewed: (p: {
    product_id: string;
    product_name: string;
    brand?: string;
    category?: string;
    kind: ProductKindTag;
    price_min_eur?: number;
    offers_count: number;
    has_3d: boolean;
  }) => capture("product_viewed", p),

  productGalleryNavigated: (p: { image_index: number; method: GalleryMethod }) =>
    capture("product_gallery_navigated", p),

  productOffersSorted: (p: { sort: string }) => capture("product_offers_sorted", p),

  productVariantSelected: (p: { product_id: string; variant: string }) =>
    capture("product_variant_selected", p),

  /** Le pont fiche → 3D : mesure si le configurateur attire depuis le catalogue. */
  productConfigureClicked: (p: { product_id: string; kind: ProductKindTag }) =>
    capture("product_configure_clicked", p),

  /**
   * Clic dans « Produits suggérés » (bas de fiche), ou dans « Comparer avec » quand
   * `block: "compare"`. `from_product_id` = la fiche
   * quittée : c'est lui qui dit si le bloc relance vraiment la visite ou s'il
   * ne fait que rallonger la page.
   */
  productSuggestionClicked: (p: { from_product_id: string; product_id: string; position: number; block?: "compare" }) =>
    capture("product_suggestion_clicked", p),

  /**
   * ⭐ MÉTRIQUE REINE — le clic « Voir chez {revendeur} ».
   * C'est la seule conversion du site : le trafic qu'on envoie aux revendeurs,
   * et l'argument pour négocier l'affiliation.
   */
  offerOutboundClicked: (p: {
    product_id: string;
    product_name: string;
    reseller_id: string;
    reseller_name: string;
    price_eur?: number;
    position: number;
    is_cheapest: boolean;
    is_affiliate: boolean;
    source: "offer_list" | "mobile_bar" | "quickview";
  }) => {
    capture("offer_outbound_clicked", p);
    setPerson({ clicked_reseller: true });
  },

  // ── Configurateur 3D ────────────────────────────────────────────────────────

  configOpened: (p: { source: ConfigSource; focus_product_id?: string; autosave_restored: boolean }) =>
    capture("config_opened", p),

  /** Temps entre l'arrivée et le moment où l'on peut vraiment jouer avec le gilet. */
  configBootCompleted: (p: { duration_ms: number }) => capture("config_boot_completed", p),

  configVestSelected: (p: { vest_id: string; vest_name: string }) =>
    capture("config_vest_selected", p),

  /**
   * ⭐ ACTIVATION — le premier accessoire posé, c'est le moment où le visiteur
   * « comprend » le produit. Tout le funnel du configurateur vise ce clic.
   */
  configAccessoryAdded: (p: {
    accessory_id: string;
    accessory_name: string;
    category?: string;
    method: AddMethod;
    vest_id: string;
    total_placed: number;
  }) => {
    capture("config_accessory_added", p);
    setPerson({ activated: true }, { first_activation_vest: p.vest_id });
  },

  configAccessoryMoved: (p: { accessory_id: string }) => capture("config_accessory_moved", p),

  configAccessoryRemoved: (p: { accessory_id: string; remaining_count: number }) =>
    capture("config_accessory_removed", p),

  configCleared: (p: { items_count: number }) => capture("config_cleared", p),

  /** Chevauchement signalé : friction UX à surveiller (pose refusée ou moche). */
  configOverlapShown: (p: { severity: string; accessory_id?: string }) =>
    capture("config_overlap_shown", p),

  configViewChanged: (p: { view: string }) => capture("config_view_changed", p),

  configSceneChanged: (p: { scene: string }) => capture("config_scene_changed", p),

  configShowcaseEntered: () => capture("config_showcase_entered"),

  configShowcaseExited: (p: { duration_s: number; sound_was_on: boolean }) =>
    capture("config_showcase_exited", p),

  /**
   * Fiche de charge ouverte = le « panier rêvé ». Le prix total des montages
   * imaginés par les visiteurs est notre meilleur indicateur de valeur.
   */
  configLoadsheetOpened: (p: {
    items_count: number;
    total_price_eur: number;
    total_weight_g?: number;
    molle_fill_pct?: number;
  }) => capture("config_loadsheet_opened", p),

  /** Résumé de fin de visite du configurateur (envoyé à la fermeture de l'onglet). */
  configSessionEnded: (p: {
    session_duration_s: number;
    placed_count: number;
    total_price_eur: number;
  }) => capture("config_session_ended", p, true),

  configLoadoutSaved: (p: { items_count: number; total_price_eur: number }) =>
    capture("config_loadout_saved", p),

  configLoadoutLoaded: (p: { loadout_id: string }) => capture("config_loadout_loaded", p),

  configLoadoutDeleted: (p: { loadout_id: string }) => capture("config_loadout_deleted", p),

  configOnboardingDone: (p: { action: "completed" | "dismissed" }) =>
    capture("config_onboarding_done", p),

  /**
   * Une main du coach gestuel s'est affichée (cf. useCoach.ts). Sert à vérifier que les gestes
   * enseignés sont bien ceux qui manquaient : `move`/`tap` montrés ↗ devraient faire monter
   * `config_accessory_moved` et `config_product_opened`.
   */
  configHintShown: (p: { step: string }) => capture("config_hint_shown", p),

  /** Tutoriel abandonné : `at_step` dit OÙ il lasse — c'est là qu'il faudra le raccourcir. */
  configTutorialSkipped: (p: { at_step: string }) => capture("config_tutorial_skipped", p),

  /** Clic depuis la 3D vers une fiche produit : la jonction « le 3D fait-il vendre ? ».
   *  `detail` = le ⓘ d'une vignette d'inventaire · `dock` = la fiche de l'objet sélectionné ·
   *  `expand` = l'agrandi (photo + revendeurs) · `loadsheet` = la fiche de charge. */
  configProductOpened: (p: { product_id: string; from: "loadsheet" | "detail" | "dock" | "expand" }) =>
    capture("config_product_opened", p),

  // ── Performance & incidents 3D ──────────────────────────────────────────────

  /** UN SEUL envoi par session (à la fermeture de l'onglet), pas de flux continu. */
  perf3dReport: (p: {
    fps_avg: number;
    fps_min: number;
    vram_est_mb: number;
    dpr: number;
    session_duration_s: number;
    placed_count: number;
  }) => capture("perf_3d_report", p, true),

  errorWebglContextLost: () => capture("error_webgl_context_lost"),

  errorGlbLoadFailed: (p: { product_id?: string; url: string }) =>
    capture("error_glb_load_failed", p),
};
